import React, { useContext, useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import ChatContext from "../../context/chat/context";
import LoadingPage from "../../components/general/layout/loading-page";
import { useLayoutEffect } from "react";
import ChatSidebar from "./components/chat-sidebar";
import MessageContainer from "./components/message-container";
import ChatInputArea from "./components/input-area";
import Button from "../../components/general/antd/button";
import AppContext from "../../context/app/context";
import { useTranslation } from "react-i18next";
const Chat: React.FC = () => {
  const {
    messages, loading, selectedChatId, query, messageQuery,
    actions: { getMessages, getData, setupSocket, setSelectedChatId, sendMessage, setMessageQuery, setQuery },
  } = useContext(ChatContext);

  useEffect(() => {
    getData();
  }, [query]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [scrollToBottomOnNewMessage, setScrollToBottomOnNewMessage] = useState(false);
  const chatsContainerRef = useRef<HTMLDivElement>(null);
  const [scrollCompensation, setScrollCompensation] = useState<number | null>(null);
  const scrollTriggeredByPagination = useRef(false);
  const { screenSize } = useContext(AppContext);
  const { t } = useTranslation();
  const isMobileOrTablet = screenSize == 'mobileOrTablet';
  useEffect(() => {
    const init = async () => {
      await getData();
      setupSocket();
    };
    init();
    const urlParams = new URLSearchParams(window.location.search);
    const chatIdFromUrl = urlParams.get("chatId");

    if (chatIdFromUrl) {
      const chatId = Number(chatIdFromUrl);
      setSelectedChatId(chatId);
      setMessageQuery({ ...messageQuery, chatId });
      setHasMoreMessages(true);
    }
  }, []);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || scrollCompensation === null) return;
    const scrollHeightAfter = container.scrollHeight;
    const delta = scrollHeightAfter - scrollCompensation;
    container.scrollTop = delta;

    setScrollCompensation(null);
  }, [messages]);

  useEffect(() => {
    if (selectedChatId) {
      getMessages();
      if (messageQuery.page == 1)
        setScrollToBottomOnNewMessage(true);
    }
  }, [selectedChatId, messageQuery.page]);

  useEffect(() => {
    if (!loading.includes("messages") && scrollToBottomOnNewMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setScrollToBottomOnNewMessage(false);
    }
  }, [messages, loading]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = async () => {
      if (container.scrollTop === 0 && hasMoreMessages && !loading.includes("messages")) {
        scrollTriggeredByPagination.current = true;
        const scrollHeightBefore = container.scrollHeight;
        const nextPage = messageQuery.page + 1;
        setScrollCompensation(scrollHeightBefore);
        setMessageQuery({ chatId: selectedChatId!, page: nextPage });
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedChatId, messageQuery.page, hasMoreMessages, loading]);

  useEffect(() => {
    const container = chatsContainerRef.current;
    if (!container) return;

    const handleChatScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 10 &&
        hasMoreChats &&
        !loading.includes("list")
      ) {
        const nextPage = query.page + 1;
        setQuery({ ...query, page: nextPage });
      }
    };

    container.addEventListener("scroll", handleChatScroll);
    return () => container.removeEventListener("scroll", handleChatScroll);
  }, [hasMoreChats, loading, query.page]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChatId]);

  return (
    <Row
      style={{
        height: "100%",
        border: "1px solid #2b363a",
        borderRadius: "8px",
        flexDirection: isMobileOrTablet ? 'column' : 'row',
      }}
    >
      <Col
        span={isMobileOrTablet ? 24 : 6}
        style={{
          height: isMobileOrTablet ? '40%' : '100%',
          borderRight: isMobileOrTablet ? 'none' : "1px solid #2b363a",
          borderBottom: isMobileOrTablet ? "1px solid #2b363a" : 'none',
          display: selectedChatId && isMobileOrTablet ? 'none' : 'block',
        }}
      >
        <ChatSidebar
          chatsContainerRef={chatsContainerRef}
          setHasMoreMessages={setHasMoreMessages}
        />
      </Col>

      <Col
        span={isMobileOrTablet ? 24 : 18}
        style={{
          height: isMobileOrTablet ? '60%' : '100%',
          display: isMobileOrTablet && !selectedChatId ? 'none' : 'flex',
          flexDirection: 'column',
        }}
      >
        {loading.includes("messages") && !messages?.data?.length ? (
          <LoadingPage />
        ) : (
          <>
            <MessageContainer
              messagesContainerRef={messagesContainerRef}
              messagesEndRef={messagesEndRef}
            />
            <ChatInputArea
              setScrollToBottomOnNewMessage={setScrollToBottomOnNewMessage}
            />
          </>
        )}
        {isMobileOrTablet && selectedChatId && (
          <Button
            type="link"
            onClick={() => {
              setSelectedChatId(null)
              window.history.pushState({}, "", window.location.pathname);
            }}
            style={{ alignSelf: 'flex-start', margin: '5px' }}
          >
            &larr; {t('back_to_chats')}
          </Button>
        )}
      </Col>
    </Row>
  );
};

export default Chat;